import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import StatCard from "../../../parts/cards/StatCard";
import CustomLoader from "../../../parts/custom-loader";
import { RillContext } from "../../../rill/RillReact/share/base";
import { formatNumber } from "../../../utils/format-sub";
import AtRiskTable from "./components/AtRiskTable";
import BandTag from "./components/BandTag";
import ContextSwitcher from "./components/ContextSwitcher";
import CsvExportLink from "./components/CsvExportLink";
import daysUntil from "./components/daysUntil";
import ExpiringPassesTable from "./components/ExpiringPassesTable";
import FailedPayoutsCallout from "./components/FailedPayoutsCallout";
import GrowthCoupons from "./components/GrowthCoupons";
import GrowthReferrals from "./components/GrowthReferrals";
import GrowthTrialFunnel from "./components/GrowthTrialFunnel";
import HorizonControl from "./components/HorizonControl";
import IncomeChart from "./components/IncomeChart";
import IncomeHeader from "./components/IncomeHeader";
import IncomeLanding from "./components/IncomeLanding";
import {
  buildChartData,
  getChartLegend,
  getStacks,
  seriesToCsv,
} from "./components/incomeChartData";
import MembersChart from "./components/MembersChart";
import PayoutsPointer from "./components/PayoutsPointer";
import PayoutsTable from "./components/PayoutsTable";
import PeriodControls, {
  formatPeriodRange,
  periodLabel,
  periodWindow,
} from "./components/PeriodControls";
import PlansTable from "./components/PlansTable";
import peopleNouns from "./components/peopleNouns";
import ReconciliationBlock from "./components/ReconciliationBlock";
import ScheduledToCancelTable from "./components/ScheduledToCancelTable";
import SourceBreakdownTable from "./components/SourceBreakdownTable";
import StatBand from "./components/StatBand";
import TopPlansTable from "./components/TopPlansTable";
import TransactionsTable from "./components/TransactionsTable";
import TrialsEndingTable from "./components/TrialsEndingTable";
import { TABS_BY_CONTEXT } from "./tabs";

const GRANULARITIES = ["month", "week", "day"];

const GranularitySelector = ({ value, onClick, t }) => (
  <nav className="toggle-options">
    {GRANULARITIES.map((granularity) => (
      <button
        key={granularity}
        type="button"
        data-button-type={value === granularity ? "outline" : "text-white"}
        onClick={(e) => onClick(e, granularity)}
      >
        <span>{t(`income.granularity.${granularity}`)}</span>
      </button>
    ))}
  </nav>
);

// Labels and date ranges for the selected period come from PeriodControls'
// periodWindow — the GUI twin of msb-api's windowFromSelector (sc-45975).
const rangeSelectorLabel = periodLabel;
// The period ends where the data does: when the last sync is earlier than
// today, "Mar 24 – Sep 24" next to "as at Sep 23" promised a day the page
// doesn't have (sc-46067).
const formatPeriodRangeText = (selector, syncedAt) => {
  const window = periodWindow(selector);
  if (!window) return null;
  const until =
    syncedAt && moment(syncedAt).isBefore(window.until)
      ? moment(syncedAt)
      : window.until;
  return formatPeriodRange({ ...window, until });
};

// The controller only checks sync status once, on the request that rendered
// this page — a backfill that completes after that has nothing to tell the
// still-open tab. Polled client-side instead, cheaply (fe-income-sync is a
// single Mongo doc read), until status leaves "backfilling", at which point
// a real page refresh loads the tab data the syncing branch skipped.
const SYNC_POLL_MS = 4000;

class View extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      passHorizonDays: 7,
      trialHorizonDays: 7,
      cancelHorizonDays: 30,
      polledSync: null,
      // Client-side only: fe-incomes returns every granularity's series,
      // so the toggle re-renders rather than reloading the page.
      granularity: GRANULARITIES.includes(props.granularity)
        ? props.granularity
        : "month",
    };
    this.pollTimer = null;
    this.handleDateSelector = this.handleDateSelector.bind(this);
    this.handleGranularitySelector = this.handleGranularitySelector.bind(this);
    this.handleTabSelector = this.handleTabSelector.bind(this);
    this.pollSyncStatus = this.pollSyncStatus.bind(this);
  }

  componentDidMount() {
    if (this.props.syncing) this.startPolling();
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling() {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(this.pollSyncStatus, SYNC_POLL_MS);
  }

  stopPolling() {
    if (!this.pollTimer) return;
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async pollSyncStatus() {
    const { api, syncQuery = {} } = this.props;
    const res = await api("fe-income-sync", { query: syncQuery });
    const sync = await res.json();
    this.setState({ polledSync: sync });
    if (sync.status !== "backfilling") {
      this.stopPolling();
      const { tab, selector, type, context, search } = this.props;
      const { granularity } = this.state;
      submit("/actions/refresh", {
        method: "GET",
        body: { tab, selector, granularity, type, context, search },
      });
    }
  }

  handleDateSelector(e, selector) {
    e.preventDefault();
    const { tab, type, context, search } = this.props;
    const { granularity } = this.state;
    submit("/actions/refresh", {
      method: "GET",
      body: { tab, selector, granularity, type, context, search },
    });
  }

  handleGranularitySelector(e, granularity) {
    e.preventDefault();
    this.setState({ granularity });
  }

  handleTabSelector(e, tab) {
    e.preventDefault();
    if (tab === this.props.tab) return;
    const { selector, context } = this.props;
    const { granularity } = this.state;
    submit("/actions/refresh", {
      method: "GET",
      body: { tab, selector, granularity, context },
    });
  }

  renderOverview() {
    const { locals } = this.context;
    const { feIncomes = {} } = locals;
    const { t, selector, context, sync = {} } = this.props;
    const { granularity } = this.state;
    const currency = feIncomes.currency || "usd";
    const {
      incomes = {},
      summary = {},
      membersTrend = null,
      sourceBreakdown = null,
      topPlans = [],
    } = feIncomes;
    const { mrr = {} } = incomes;
    const series = feIncomes.seriesByGranularity?.[granularity] || [];
    const {
      newMembers = 0,
      churnedMembers = 0,
      passesSold = 0,
      atRisk = {},
      membership = {},
    } = summary;
    const money = (value) => formatNumber(value || 0, currency);
    const rangeLabel = rangeSelectorLabel(t, selector);
    const revenueChartLegend = getChartLegend(
      buildChartData(series),
      getStacks(context),
      t
    );
    const revenueChartCsv = seriesToCsv(series, t);

    // Requirements §5.1: which tiles are true right now varies by context —
    // PRESS has no trial concept, PUSH has no pass concept, and All can't
    // show either without double-counting a person who holds both.
    const mrrTile = (
      <StatCard
        title={t("income.monthlyRecurringRevenue")}
        tooltipText={t("income.mrrTooltip")}
        current={mrr.actualMonth || 0}
        previous={null}
        formatValue={money}
        caption={t("income.snapshot.fromSubscriptions", {
          count: membership.activeAtEnd || 0,
        })}
      />
    );
    const atRiskTile = (
      <StatCard
        title={t("income.atRisk")}
        tooltipText={t("income.atRiskTooltip", {
          people: t(peopleNouns(context).plural),
        })}
        current={atRisk.count || 0}
        previous={null}
        caption={t("income.snapshot.atRiskAmount", {
          amount: money(atRisk.amount || 0),
        })}
        highlight={(atRisk.count || 0) > 0}
      />
    );
    const snapshotTiles = {
      press: (
        <>
          <StatCard
            title={t("income.activeMembers")}
            current={membership.activeAtEnd || 0}
            previous={null}
            caption={t("income.snapshot.payingToday")}
          />
          {mrrTile}
          <StatCard
            title={t("income.onAPass")}
            current={summary.activePasses || 0}
            previous={null}
          />
          {atRiskTile}
        </>
      ),
      push: (
        <>
          <StatCard
            title={t("income.activeSubscribers")}
            current={membership.activeAtEnd || 0}
            previous={null}
            caption={t("income.snapshot.payingToday")}
          />
          {mrrTile}
          <StatCard
            title={t("income.onTrial")}
            current={summary.onTrial || 0}
            previous={null}
          />
          {atRiskTile}
        </>
      ),
      all: (
        <>
          <StatCard
            title={t("income.payingPeople")}
            tooltipText={t("income.payingPeopleTooltip")}
            current={summary.payingPeople || 0}
            previous={null}
          />
          {mrrTile}
        </>
      ),
    };

    const periodExtra = {
      press: (
        <StatCard
          title={t("income.passesSold")}
          tooltipText={t("income.passesSoldTooltip")}
          current={passesSold}
          previous={null}
        />
      ),
      push: (
        <StatCard
          title={t("income.trialsConverted")}
          current={summary.trialsConverted || 0}
          previous={null}
        />
      ),
      all: null,
    };

    return (
      <>
        <StatBand
          kind="snapshot"
          asAt={sync.newestSynced}
          title={t("income.bands.snapshot")}
          description={t("income.bands.snapshotDescription")}
          t={t}
        >
          {snapshotTiles[context]}
        </StatBand>

        <p className="reconciliation-line">
          {t(
            context === "push"
              ? "income.reconciliationPush"
              : "income.reconciliation",
            {
              start: membership.activeAtStart || 0,
              new: membership.new || 0,
              churned: membership.churned || 0,
              end: membership.activeAtEnd || 0,
            }
          )}
        </p>

        <StatBand
          kind="period"
          rangeLabel={rangeLabel}
          periodRangeText={formatPeriodRangeText(selector, sync.newestSynced)}
          title={t("income.bands.period")}
          description={t(
            /^year-/.test(selector)
              ? "income.bands.periodDescriptionYear"
              : "income.bands.periodDescription",
            { range: rangeLabel }
          )}
          t={t}
        >
          <StatCard
            title={t("income.netRevenue")}
            current={summary.netRevenue || 0}
            previous={null}
            formatValue={money}
            caption={
              summary.multiCurrency
                ? t("income.netRevenueMultiCurrencyTooltip", {
                    currency: currency.toUpperCase(),
                  })
                : summary.revenueBreakdown &&
                  // What the coach keeps, and how it's reached (sc-46058).
                  t("income.netRevenueBreakdown", {
                    gross: money(summary.revenueBreakdown.gross),
                    refunds: money(summary.revenueBreakdown.refunds),
                    msbFee: money(summary.revenueBreakdown.msbFee),
                    stripeFees: money(summary.revenueBreakdown.stripeFees),
                  })
            }
          />
          <StatCard
            title={t(peopleNouns(context).new)}
            tooltipText={t(peopleNouns(context).newTooltip)}
            current={newMembers}
            previous={null}
          />
          <StatCard
            title={t(peopleNouns(context).churned)}
            tooltipText={t(peopleNouns(context).churnedTooltip)}
            current={churnedMembers}
            previous={null}
            caption={
              membership.activeAtStart
                ? t("income.period.churnedOfStart", {
                    pct: (
                      (churnedMembers / membership.activeAtStart) *
                      100
                    ).toFixed(1),
                    start: membership.activeAtStart,
                  })
                : undefined
            }
          />
          {periodExtra[context]}
        </StatBand>

        {context === "all" && feIncomes.unattributed?.revenue > 0 && (
          <p className="unattributed-note">
            {t("income.unattributedNote", {
              amount: money(feIncomes.unattributed.revenue),
              count: feIncomes.unattributed.transactionCount,
            })}
          </p>
        )}

        <section className="incomes-container">
          <div className="overview-header">
            <div className="flex gap-2 items-center">
              <h3>{t("income.revenueSection")}</h3>
              <BandTag kind="period" label={t("income.bands.period")} />
            </div>
            <div className="flex gap-4 items-center">
              <div className="chart-legend__items">
                {revenueChartLegend.items.map(({ key, fill, label }) => (
                  <span key={key} className="chart-legend__item">
                    <span
                      className="chart-legend__swatch"
                      style={{ background: fill }}
                    />
                    {label}
                  </span>
                ))}
              </div>
              <GranularitySelector
                value={granularity}
                onClick={this.handleGranularitySelector}
                t={t}
              />
              <CsvExportLink
                csv={series.length > 0 ? revenueChartCsv : ""}
                filename="revenue.csv"
                t={t}
              />
            </div>
          </div>
          <IncomeChart
            series={series}
            currency={currency}
            context={context}
            t={t}
          />
        </section>

        {context === "press" && membersTrend?.length > 0 && (
          <section className="incomes-container mt-6">
            <div className="overview-header">
              <div className="flex gap-2 items-center">
                <h3>{t("income.membersChartTitle")}</h3>
                <BandTag kind="period" label={t("income.bands.period")} />
              </div>
            </div>
            <MembersChart trend={membersTrend} />
          </section>
        )}

        {(topPlans.length > 0 || (context === "push" && sourceBreakdown)) && (
          <div className="overview-tables">
            {topPlans.length > 0 && (
              <section className="incomes-container">
                <TopPlansTable
                  plans={topPlans}
                  currency={currency}
                  showProduct={context === "all"}
                  t={t}
                />
              </section>
            )}
            {context === "push" && sourceBreakdown && (
              <section className="incomes-container">
                <SourceBreakdownTable
                  breakdown={sourceBreakdown}
                  currency={currency}
                  t={t}
                />
              </section>
            )}
          </div>
        )}
      </>
    );
  }

  renderPlans() {
    const { locals } = this.context;
    const { planPerformance = {} } = locals;
    const { t, selector, plansCsv, sync = {} } = this.props;
    const currency = planPerformance.currency || "usd";
    const { plans = [] } = planPerformance;
    const rangeLabel = rangeSelectorLabel(t, selector);

    return (
      <section className="incomes-container plans-card">
        <PlansTable
          plans={plans}
          currency={currency}
          csvString={plansCsv}
          showProduct={this.props.context === "all"}
          asOfLabel={t("income.bands.asAt", {
            time: sync.newestSynced
              ? moment(sync.newestSynced).format("HH:mm")
              : "—",
          })}
          periodLabel={t("income.bands.forRange", { range: rangeLabel })}
          rangeLabel={rangeLabel}
        />
      </section>
    );
  }

  renderAttention() {
    const { locals } = this.context;
    // Same gates the community chat page uses: that product's in-app
    // community is on, and direct messages aren't switched off (they live
    // on pushAccountInfo and default to on). Read off the org owner for a
    // sub-coach — it's the owner's community.
    const { pushAccountInfo = {}, pressAccountInfo = {} } =
      locals.activeUserToRequest || {};
    const dmsOn = pushAccountInfo.directMessagesEnabled !== false;
    const dmEnabled = {
      push: dmsOn && !!pushAccountInfo.inAppCommunityEnabled,
      press: dmsOn && !!pressAccountInfo.inAppCommunityEnabled,
    };
    const { failedPayments = {} } = locals;
    const {
      members = [],
      expiringPasses = [],
      failedPayouts = null,
      scheduledToCancel = { count: 0, rows: [], mrr: 0 },
      trialsEnding = null,
      mrrAtRisk = { suspended: 0, scheduledToCancel: 0, total: 0 },
      totalAtRisk = 0,
      horizonCeilingDays = 30,
      currency = "usd",
    } = failedPayments;
    const { t, sync = {}, context } = this.props;
    const { passHorizonDays, trialHorizonDays, cancelHorizonDays } = this.state;

    // Requirements §6.4: filtering is client-side over rows already
    // fetched, so changing a horizon never triggers a new request. Calendar
    // days in the viewer's timezone, same as the tables show (sc-46066).
    const withinDays = (rows, days) =>
      rows.filter((row) => {
        const remaining = daysUntil(row.trialEnd || row.endsAt);
        return remaining == null || remaining <= days;
      });

    const visibleScheduled = withinDays(
      scheduledToCancel.rows,
      cancelHorizonDays
    );
    const visibleTrials = withinDays(
      trialsEnding?.rows || [],
      trialHorizonDays
    );
    const snapshotBadge = (
      <BandTag kind="snapshot" label={t("income.bands.now")} />
    );

    const visiblePasses = expiringPasses.filter((pass) => {
      const days = Math.ceil(
        (new Date(pass.periodEnd).getTime() - Date.now()) / 86400000
      );
      return days <= passHorizonDays;
    });

    return (
      <>
        <FailedPayoutsCallout
          failedPayouts={failedPayouts}
          currency={currency}
          t={t}
        />
        <StatBand
          kind="snapshot"
          asAt={sync.newestSynced}
          title={t("income.bands.snapshot")}
          description={t("income.attention.bandDescription")}
          metaNote={t("income.attention.bandNoSelector")}
          t={t}
        >
          <StatCard
            title={t("income.attention.tiles.failedPayments")}
            badge={snapshotBadge}
            tooltipText={t("income.attention.tiles.failedPaymentsTooltip", {
              amount: formatNumber(totalAtRisk, currency),
            })}
            current={members.length}
          />
          <StatCard
            title={t("income.attention.tiles.scheduledToCancel")}
            badge={snapshotBadge}
            tooltipText={t("income.attention.tiles.scheduledToCancelTooltip")}
            // Each horizon tile counts what its list shows, and says the
            // window (sc-46066).
            current={visibleScheduled.length}
            caption={t("income.attention.tiles.nextDays", {
              count: cancelHorizonDays,
            })}
          />
          {context === "press" ? (
            <StatCard
              title={t("income.attention.tiles.expiringPasses")}
              badge={snapshotBadge}
              tooltipText={t("income.attention.tiles.expiringPassesTooltip")}
              current={visiblePasses.length}
              caption={t("income.attention.tiles.nextDays", {
                count: passHorizonDays,
              })}
            />
          ) : (
            <StatCard
              title={t("income.attention.tiles.trialsEnding")}
              badge={snapshotBadge}
              tooltipText={t("income.attention.tiles.trialsEndingTooltip")}
              current={visibleTrials.length}
              caption={[
                t("income.attention.tiles.nextDays", {
                  count: trialHorizonDays,
                }),
                t("income.attention.tiles.setToCancel", {
                  count: visibleTrials.filter((row) => row.cancelAtPeriodEnd)
                    .length,
                }),
              ].join(" · ")}
            />
          )}
          <StatCard
            title={t("income.attention.tiles.mrrAtRisk")}
            badge={snapshotBadge}
            tooltipText={t("income.attention.tiles.mrrAtRiskTooltip")}
            current={mrrAtRisk.total}
            formatValue={(v) => formatNumber(Number(v), currency)}
          />
        </StatBand>
        <p className="p2 mt-3 text-sm">
          {t("income.attention.mrrSplitNote", {
            scheduled: formatNumber(mrrAtRisk.scheduledToCancel, currency),
            suspended: formatNumber(mrrAtRisk.suspended, currency),
          })}
        </p>
        <section className="incomes-container attention-card">
          <AtRiskTable
            members={members}
            currency={currency}
            context={context}
            theme={locals.activeUser.webAppTheme || "dark"}
            dmEnabled={dmEnabled}
          />
        </section>
        <section className="incomes-container attention-card">
          <ScheduledToCancelTable
            rows={visibleScheduled}
            currency={currency}
            context={context}
            theme={locals.activeUser.webAppTheme || "dark"}
            dmEnabled={dmEnabled}
            controls={
              <HorizonControl
                value={cancelHorizonDays}
                ceilingDays={horizonCeilingDays}
                onChange={(days) => this.setState({ cancelHorizonDays: days })}
              />
            }
          />
        </section>
        {context === "push" && trialsEnding && (
          <section className="incomes-container attention-card">
            <TrialsEndingTable
              rows={visibleTrials}
              context={context}
              theme={locals.activeUser.webAppTheme || "dark"}
              controls={
                <HorizonControl
                  value={trialHorizonDays}
                  ceilingDays={horizonCeilingDays}
                  onChange={(days) => this.setState({ trialHorizonDays: days })}
                />
              }
            />
          </section>
        )}
        {context === "press" && (
          <section className="incomes-container attention-card">
            <ExpiringPassesTable
              passes={visiblePasses}
              horizonDays={passHorizonDays}
              context={context}
              controls={
                <HorizonControl
                  value={passHorizonDays}
                  ceilingDays={horizonCeilingDays}
                  onChange={(days) => this.setState({ passHorizonDays: days })}
                />
              }
            />
          </section>
        )}
      </>
    );
  }

  renderGrowth() {
    const { locals } = this.context;
    const { growth = {} } = locals;
    const {
      trialFunnel = null,
      coupons = [],
      couponSummary = null,
      referrals = null,
    } = growth;
    const { t, selector, context } = this.props;
    // PRESS has no trials or referrals; its Growth tab is coupons only.
    const isPush = context === "push";
    const currency = growth.currency || "usd";
    const rangeLabel = rangeSelectorLabel(t, selector);
    // Requirements §5.1: a Growth row mixes snapshot and period tiles, so
    // the marker rides on each tile rather than on a band header.
    const badges = {
      now: <BandTag kind="snapshot" label={t("income.bands.now")} />,
      period: <BandTag kind="period" label={rangeLabel} />,
    };

    return (
      <>
        {isPush && (
          <GrowthTrialFunnel
            data={trialFunnel}
            currency={currency}
            badges={badges}
            t={t}
          />
        )}
        <GrowthCoupons
          rows={coupons}
          summary={couponSummary}
          currency={currency}
          badges={badges}
          rangeLabel={rangeLabel}
          context={context}
          t={t}
        />
        {isPush && <GrowthReferrals data={referrals} t={t} />}
      </>
    );
  }

  renderPayouts() {
    const { locals } = this.context;
    const { payouts: payoutsData = {} } = locals;
    const {
      t,
      selector,
      context,
      contexts = [],
      sync = {},
      api,
      syncQuery = {},
    } = this.props;
    const {
      payouts = [],
      reconciliation = null,
      nextPayout = null,
      currency = "usd",
      hasMore = false,
      nextStartingAfter = null,
      total = 0,
    } = payoutsData;
    const rangeLabel = rangeSelectorLabel(t, selector);

    // Single-product coaches never have context === "all" — resolveContext
    // (income.controller.js) canonicalizes them straight to their one
    // product, and ContextSwitcher never offers "all" below 2 options. They
    // must land on the SAME branch an "all" context does (reconciliation +
    // full table), never the pointer — matching fe-payouts.js's gate, which
    // now computes the reconciliation for this same population.
    const showFullPayouts = context === "all" || contexts.length <= 1;

    return (
      <section className="incomes-container payouts-card">
        <header className="table-header justify-start! gap-2">
          <hgroup>
            <h3 className="title">{t("income.payouts.title")}</h3>
          </hgroup>
          <div className="flex gap-2 items-center">
            <BandTag
              kind="period"
              label={t("income.bands.forRange", { range: rangeLabel })}
            />
            {/* Balance transactions arrive on the nightly incremental, not
                by webhook like payouts do (§3.4) — so a payout can show
                here before its fee detail does. Say how fresh this is. */}
            <BandTag
              kind="snapshot"
              label={t("income.bands.asAt", {
                time: sync.newestSynced
                  ? moment(sync.newestSynced).format("HH:mm")
                  : "—",
              })}
            />
          </div>
        </header>
        <hr className="mt-4" />
        {showFullPayouts ? (
          <>
            {(reconciliation || []).map((block) => (
              <ReconciliationBlock key={block.currency} block={block} t={t} />
            ))}
            <PayoutsTable
              payouts={payouts}
              hasMore={hasMore}
              nextStartingAfter={nextStartingAfter}
              total={total}
              currency={currency}
              selector={selector}
              context={context}
              organizationUser={syncQuery.organizationUser}
              api={api}
            />
          </>
        ) : (
          <PayoutsPointer
            payouts={payouts}
            nextPayout={nextPayout}
            currency={currency}
            t={t}
          />
        )}
      </section>
    );
  }

  renderTransactions() {
    const { locals } = this.context;
    const { transactions: transactionsData = {} } = locals;
    const { t, selector, api, context, syncQuery = {} } = this.props;
    const {
      transactions = [],
      hasMore = false,
      nextStartingAfter = null,
      currency = "usd",
    } = transactionsData;
    const rangeLabel = rangeSelectorLabel(t, selector);

    return (
      <section className="incomes-container transactions-card">
        <TransactionsTable
          transactions={transactions}
          hasMore={hasMore}
          nextStartingAfter={nextStartingAfter}
          currency={currency}
          selector={selector}
          context={context}
          organizationUser={syncQuery.organizationUser}
          periodLabel={t("income.bands.forRange", { range: rangeLabel })}
          theme={locals.activeUser.webAppTheme || "dark"}
          api={api}
        />
      </section>
    );
  }

  renderSyncStatus() {
    const { t, sync = {} } = this.props;
    const { newestSynced, lastError, status } = sync;

    // Requirements §2.1: every screen states when its data was last synced,
    // and a lagging/failed sync says so rather than quietly showing stale
    // figures as current — a wrong number presented confidently is worse
    // than an honest gap.
    if (status === "failed" || lastError) {
      return (
        <div className="sync-status sync-status--error">
          {t("income.syncFailed")}
        </div>
      );
    }
    if (!newestSynced) return null;
    return (
      <div className="sync-status">
        {t("income.syncedAgo", { time: moment(newestSynced).fromNow() })}
      </div>
    );
  }

  render() {
    const { t, tab, syncing, sync = {} } = this.props;

    if (syncing) {
      const { polledSync } = this.state;
      return (
        <main className="incomes">
          <CustomLoader
            startedAt={polledSync?.backfillStartedAt || sync.backfillStartedAt}
          />
        </main>
      );
    }

    const { context, contexts = [], landing, selector } = this.props;
    const { granularity } = this.state;

    if (!context) {
      return (
        <main className="incomes">
          {this.renderSyncStatus()}
          <IncomeLanding landing={landing} t={t} />
        </main>
      );
    }

    const { locals } = this.context;
    const currency =
      locals.feIncomes?.currency ||
      locals.planPerformance?.currency ||
      locals.growth?.currency ||
      locals.failedPayments?.currency ||
      locals.payouts?.currency ||
      locals.transactions?.currency ||
      "usd";
    // PUSH and PRESS sell through the same connected account —
    // pressAccountInfo has no connectedAccountId, which hid the button on
    // PRESS (sc-46000). A sub-coach sees the org owner's numbers but can't
    // open the owner's Stripe dashboard, so the button is owner-only.
    const { activeUser, activeUserToRequest } = locals;
    const isSubCoach =
      String(activeUser._id) !== String(activeUserToRequest?._id);
    const connectedAccountId = isSubCoach
      ? null
      : activeUser.pushAccountInfo?.connectedAccountId;

    const tabs = TABS_BY_CONTEXT[context];

    return (
      <main className="incomes">
        {this.renderSyncStatus()}
        <ContextSwitcher
          context={context}
          contexts={contexts}
          tab={tab}
          selector={selector}
          granularity={granularity}
          t={t}
        />
        <IncomeHeader
          context={context}
          currency={currency}
          connectedAccountId={connectedAccountId}
          t={t}
        />
        <header className="tabs-header">
          {/* biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the global tab styles key off nav[role="tablist"] */}
          <nav role="tablist">
            {tabs.map((value) => (
              <a
                key={value}
                href={`/coaching-portal/income?${new URLSearchParams({
                  tab: value,
                  selector: String(selector ?? 3),
                  granularity: this.state.granularity,
                  context,
                })}`}
                aria-current={tab === value ? true : undefined}
                onClick={(e) => this.handleTabSelector(e, value)}
              >
                {t(`income.tabs.${value}`)}
              </a>
            ))}
          </nav>
          {/* Requirements §5.1 / mockup: Attention is a worklist of what is
              unresolved right now, so a date range can only make it wrong —
              the selector is absent rather than present-but-inert. */}
          {tab !== "attention" && (
            <PeriodControls
              selector={selector}
              onSelect={this.handleDateSelector}
              t={t}
            />
          )}
        </header>
        {tab === "overview" && this.renderOverview()}
        {tab === "plans" && this.renderPlans()}
        {tab === "growth" && tabs.includes("growth") && this.renderGrowth()}
        {tab === "attention" &&
          tabs.includes("attention") &&
          this.renderAttention()}
        {tab === "transactions" &&
          tabs.includes("transactions") &&
          this.renderTransactions()}
        {tab === "payouts" && tabs.includes("payouts") && this.renderPayouts()}
      </main>
    );
  }
}

View.contextType = RillContext;

export default withTranslation("coaching-portal")(View);
